import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { RootState } from "app/store";
import type {
  CollectionCounters,
  CollectionsFilters
} from "services/types/collections.type";

export const DEFAULT_CUSTOM_FILTERS: Partial<CollectionsFilters> = {};

interface CollectionsFiltersState {
  quickFilterCode:
    | "ALL"
    | "ACTIVE"
    | "PAUSED"
    | "DISPUTED"
    | "PAID"
    | "INACTIVE";
  cadenceCompletedOnly: boolean;
  selectedCollectionIds: Record<number, boolean>;
  showCheckboxes: boolean;
  sortField: string;
  sortDirection: "asc" | "desc";
  page: number;
  pageSize: number;
  searchValue: string;
  customFilters: Partial<CollectionsFilters>;
  queriesCounters: Record<string, CollectionCounters | undefined>;
}

const initialState: CollectionsFiltersState = {
  quickFilterCode: "ALL",
  cadenceCompletedOnly: false,
  selectedCollectionIds: {},
  showCheckboxes: false,
  sortField: "days_in_collection",
  sortDirection: "desc",
  page: 0,
  pageSize: 25,
  searchValue: "",
  customFilters: { ...DEFAULT_CUSTOM_FILTERS },
  queriesCounters: {
    ALL: undefined,
    ACTIVE: undefined,
    PAUSED: undefined,
    DISPUTED: undefined,
    PAID: undefined,
    INACTIVE: undefined
  }
};

const collectionsFiltersSlice = createSlice({
  name: "collectionsFilters",
  initialState,
  reducers: {
    applyQuickFilter(
      state,
      action: PayloadAction<CollectionsFiltersState["quickFilterCode"]>
    ) {
      state.quickFilterCode = action.payload;
      // The quick filters and the cadence-completed toggle both own filters.status.
      // Clearing here -- rather than in the component -- means every path that picks a
      // quick filter releases the toggle, including ones added later.
      state.cadenceCompletedOnly = false;
      state.selectedCollectionIds = {};
      state.page = 0;
    },
    setCadenceCompletedOnly(state, action: PayloadAction<boolean>) {
      state.cadenceCompletedOnly = action.payload;
      if (action.payload) {
        // "ALL" contributes no status of its own, leaving the toggle sole owner.
        state.quickFilterCode = "ALL";
      }
      state.selectedCollectionIds = {};
      state.page = 0;
    },
    toggleShowCheckboxes(state) {
      state.showCheckboxes = !state.showCheckboxes;
      if (!state.showCheckboxes) {
        state.selectedCollectionIds = {};
      }
    },
    toggleCollectionSelection(state, action: PayloadAction<number>) {
      const id = action.payload;
      if (state.selectedCollectionIds[id]) {
        delete state.selectedCollectionIds[id];
      } else {
        state.selectedCollectionIds[id] = true;
      }
    },
    selectAllCollections(state, action: PayloadAction<number[]>) {
      action.payload.forEach((id) => {
        state.selectedCollectionIds[id] = true;
      });
    },
    clearSelectedCollections(state) {
      state.selectedCollectionIds = {};
    },
    resetFilters(state) {
      return initialState;
    },
    setSort(
      state,
      action: PayloadAction<{ field: string; direction: "asc" | "desc" }>
    ) {
      state.sortField = action.payload.field;
      state.sortDirection = action.payload.direction;
      state.page = 0; // Reset to first page when sorting changes
    },
    setPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.page = 0;
    },
    setSearchValue(state, action: PayloadAction<string>) {
      state.searchValue = action.payload;
      state.page = 0;
    },
    setCustomFilter(
      state,
      action: PayloadAction<{
        field: keyof CollectionsFilters;
        value: any;
      }>
    ) {
      if (action.payload.value === undefined || action.payload.value === null) {
        delete state.customFilters[action.payload.field];
      } else {
        state.customFilters[action.payload.field] = action.payload.value;
      }
      state.page = 0;
    },
    setCustomFilters(
      state,
      action: PayloadAction<Partial<CollectionsFilters>>
    ) {
      state.customFilters = action.payload;
      state.page = 0;
    },
    clearCustomFilters(state) {
      state.customFilters = { ...DEFAULT_CUSTOM_FILTERS };
      state.page = 0;
    },
    setQueriesCounters(
      state,
      action: PayloadAction<Record<string, CollectionCounters | undefined>>
    ) {
      state.queriesCounters = action.payload;
    }
  }
});

export const selectQuickFilterCode = (state: RootState) =>
  state.collectionsFilters.quickFilterCode;

export const selectCadenceCompletedOnly = (state: RootState) =>
  state.collectionsFilters.cadenceCompletedOnly;

export const selectSelectedCollectionIds = (state: RootState) =>
  state.collectionsFilters.selectedCollectionIds;

export const selectShowCheckboxes = (state: RootState) =>
  state.collectionsFilters.showCheckboxes;

export const selectSelectedCollectionCount = (state: RootState) =>
  Object.keys(state.collectionsFilters.selectedCollectionIds).length;

export const selectSortField = (state: RootState) =>
  state.collectionsFilters.sortField;

export const selectSortDirection = (state: RootState) =>
  state.collectionsFilters.sortDirection;

export const selectPage = (state: RootState) => state.collectionsFilters.page;

export const selectPageSize = (state: RootState) =>
  state.collectionsFilters.pageSize;

export const selectSearchValue = (state: RootState) =>
  state.collectionsFilters.searchValue;

export const selectCustomFilters = (state: RootState) =>
  state.collectionsFilters.customFilters;
export const selectQueriesCounters = (state: RootState) =>
  state.collectionsFilters.queriesCounters;

export const {
  applyQuickFilter,
  setCadenceCompletedOnly,
  toggleShowCheckboxes,
  toggleCollectionSelection,
  selectAllCollections,
  clearSelectedCollections,
  resetFilters,
  setSort,
  setPage,
  setPageSize,
  setSearchValue,
  setCustomFilter,
  setCustomFilters,
  clearCustomFilters,
  setQueriesCounters
} = collectionsFiltersSlice.actions;

export default collectionsFiltersSlice.reducer;
